import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { FaCheckCircle } from "react-icons/fa";
import { LabelValueRow, InfoPanel } from '../../';
import { useToast } from '../../../context/ToastContext';

const ReceiptStep = forwardRef(({ 
    onStartCharging,
    isProcessing,
    stationMeta,
    authData
}, ref) => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const fullNameRef = useRef(null);
    const emailRef = useRef(null);
    
    // Toast for user-friendly error feedback
    const { showToast } = useToast();

    // Pre-fill from user profile if available (could be passed as props)
    useEffect(() => {
        // If you have user profile data, pre-fill here
        // setFullName(userProfile?.name || '');
        // setEmail(userProfile?.email || '');
    }, []);

    const isValidEmail = (email) => {
        const trimmed = email.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(trimmed);
    };

    const validateForm = () => {
        const newErrors = {};
        
        const trimmedName = fullName.trim();
        if (!trimmedName) {
            newErrors.fullName = 'Full name is required';
        } else if (trimmedName.length < 2) {
            newErrors.fullName = 'Name must be at least 2 characters';
        }
        
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            newErrors.email = 'Email address is required';
        } else if (!isValidEmail(trimmedEmail)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        return newErrors;
    };

    const validateField = (fieldName, value) => {
        const newErrors = {};
        
        if (fieldName === 'fullName') {
            const trimmed = value.trim();
            if (!trimmed) {
                newErrors.fullName = 'Full name is required';
            } else if (trimmed.length < 2) {
                newErrors.fullName = 'Name must be at least 2 characters';
            }
        } else if (fieldName === 'email') {
            const trimmed = value.trim();
            if (!trimmed) {
                newErrors.email = 'Email address is required';
            } else if (!isValidEmail(trimmed)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }
        
        return newErrors[fieldName] || '';
    };

    const handleFullNameChange = (e) => {
        const value = e.target.value;
        setFullName(value);
        
        if (attemptedSubmit) {
            const error = validateField('fullName', value);
            setErrors(prev => ({ ...prev, fullName: error }));
        }
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        
        if (attemptedSubmit) {
            const error = validateField('email', value);
            setErrors(prev => ({ ...prev, email: error }));
        }
    };

    const focusFirstInvalidField = (validationErrors) => {
        const fieldOrder = ['fullName', 'email'];
        const refMap = {
            fullName: fullNameRef,
            email: emailRef
        };

        for (const fieldName of fieldOrder) {
            if (validationErrors[fieldName]) {
                const fieldRef = refMap[fieldName];
                if (fieldRef?.current) {
                    fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    fieldRef.current.focus();
                    break;
                }
            }
        }
    };

    const getToastErrorMessage = (validationErrors) => {
        // Priority order: fullName first, then email
        if (validationErrors.fullName) {
            const trimmedName = fullName.trim();
            if (!trimmedName) {
                return 'Please enter your full name.';
            } else if (trimmedName.length < 2) {
                return 'Please enter a valid full name (at least 2 characters).';
            }
        }
        
        if (validationErrors.email) {
            const trimmedEmail = email.trim();
            if (!trimmedEmail) {
                return 'Please enter your email address.';
            } else if (!isValidEmail(trimmedEmail)) {
                return 'Please enter a valid email address.';
            }
        }
        
        // Fallback message
        return 'Please complete all required fields.';
    };

    const handleSubmit = async () => {
        if (isProcessing) return;

        setAttemptedSubmit(true);
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            focusFirstInvalidField(validationErrors);
            
            // Show toast notification with user-friendly error message
            const errorMessage = getToastErrorMessage(validationErrors);
            showToast({
                message: errorMessage,
                type: 'error',
                duration: 4000
            });
            
            return;
        }

        // Call parent handler with receipt info
        await onStartCharging({
            fullName: fullName.trim(),
            email: email.trim()
        });
    };

    // Expose submit method to parent via ref
    useImperativeHandle(ref, () => ({
        submit: handleSubmit
    }));

    // Get last 4 digits of card if available
    const last4 = authData?.cardLast4 || '••••';

    return (
        <div className="step-container receipt-step">
            <div className="step-header">
                <h2 className='step-title'>Receipt & Confirmation</h2>
                <p className="step-subtitle">
                    Your payment is authorized. Enter your details to receive a receipt and start charging.
                </p>
            </div>

            {attemptedSubmit && Object.keys(errors).length > 0 && (
                <div className="error-summary">
                    <p className="error-summary-text">Please enter receipt details to continue.</p>
                </div>
            )}

            <div className="step-card">
                {/* Authorization Summary - Receipt Style */}
                <div className="auth-summary-section">
                    <div className="auth-status-badge">
                        <FaCheckCircle className="success-icon" />
                        <span>Authorization Placed</span>
                    </div>
                    
                    <InfoPanel>
                        {stationMeta?.name && (
                            <LabelValueRow label="Charger" value={stationMeta.name} />
                        )}
                        {stationMeta?.location && (
                            <LabelValueRow label="Location" value={stationMeta.location} />
                        )}
                        <LabelValueRow label="Payment Method" value={`Card •••• ${last4}`} />
                    </InfoPanel>
                </div>

                {/* Receipt Information Form */}
                <div className="receipt-form-container">
                    <h4 className="section-title">Receipt Information</h4>
                    <p className="section-helper">Your receipt and charging details will be emailed to you.</p>
                    
                    <form
                        className="receipt-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                    >
                        <div className="form-group">
                            <label htmlFor="fullName">
                                Full Name <span className="required">*</span>
                            </label>
                            <input
                                ref={fullNameRef}
                                id="fullName"
                                type="text"
                                placeholder="John Doe"
                                value={fullName}
                                onChange={handleFullNameChange}
                                onBlur={() => {
                                    if (attemptedSubmit) {
                                        const error = validateField('fullName', fullName);
                                        setErrors(prev => ({ ...prev, fullName: error }));
                                    }
                                }}
                                className={errors.fullName ? 'error' : ''}
                                disabled={isProcessing}
                                autoFocus
                                enterKeyHint="next"
                            />
                            {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">
                                Email Address <span className="required">*</span>
                            </label>
                            <input
                                ref={emailRef}
                                id="email"
                                type="email"
                                placeholder="john@example.com"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={() => {
                                    if (attemptedSubmit) {
                                        const error = validateField('email', email);
                                        setErrors(prev => ({ ...prev, email: error }));
                                    }
                                }}
                                className={errors.email ? 'error' : ''}
                                disabled={isProcessing}
                                enterKeyHint="done"
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>
                        {/* Hidden submit button to enable form submission from keyboard */}
                        <button type="submit" hidden />
                    </form>
                </div>
            </div>
        </div>
    );
});

ReceiptStep.displayName = 'ReceiptStep';

export default ReceiptStep;
